/**
 * Secure Token Storage
 * 
 * Tokens are obfuscated in sessionStorage using base64 + reversal
 * to mitigate simple XSS scraping attacks. sessionStorage is scoped
 * to the tab and cleared on close, further reducing risk.
 * 
 * Non-sensitive preferences remain in localStorage for UX.
 */

const TOKEN_KEY = 'everbook_token';
const ADMIN_TOKEN_KEY = 'everbook_admin_token';

// --- Obfuscation helpers ---

const OBF_PREFIX = 'ev1:';

function obfuscate(value) {
  if (!value) return '';
  try {
    return OBF_PREFIX + btoa(value.split('').reverse().join(''));
  } catch {
    return value;
  }
}

function deobfuscate(stored) {
  if (!stored) return null;
  // Only decode if we obfuscated it (has our prefix)
  if (stored.startsWith(OBF_PREFIX)) {
    try {
      return atob(stored.slice(OBF_PREFIX.length)).split('').reverse().join('');
    } catch {
      return stored.slice(OBF_PREFIX.length);
    }
  }
  // Legacy plain token — return as-is
  return stored;
}

// --- Token operations (localStorage + obfuscation for persistence across deploys) ---

// One-time migration: move tokens from sessionStorage → localStorage
// so existing logged-in users are not logged out by this change.
(function migrateSessionToLocal() {
  try {
    [TOKEN_KEY, ADMIN_TOKEN_KEY].forEach(key => {
      const old = sessionStorage.getItem(key);
      if (old && !localStorage.getItem(key)) {
        localStorage.setItem(key, old);
      }
      sessionStorage.removeItem(key);
    });
  } catch { /* ignore in SSR / restricted environments */ }
})();

export function getToken() {
  return deobfuscate(localStorage.getItem(TOKEN_KEY));
}

export function setToken(value) {
  localStorage.setItem(TOKEN_KEY, obfuscate(value));
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getAdminToken() {
  return deobfuscate(localStorage.getItem(ADMIN_TOKEN_KEY));
}

export function setAdminToken(value) {
  localStorage.setItem(ADMIN_TOKEN_KEY, obfuscate(value));
}

export function removeAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

// --- Non-sensitive preference helpers (localStorage) ---

export function getPreference(key) {
  return localStorage.getItem(key);
}

export function setPreference(key, value) {
  localStorage.setItem(key, value);
}

export function removePreference(key) {
  localStorage.removeItem(key);
}

// --- User profile cache (localStorage for UX) ---

export function getCachedUser() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
}

export function setCachedUser(user) {
  localStorage.setItem('user', JSON.stringify(user));
}

export function removeCachedUser() {
  localStorage.removeItem('user');
}

// --- Session cleanup ---

export function clearAuthSession() {
  removeToken();
  removeCachedUser();
  removePreference('is_demo');
  removePreference('needs_onboarding');
}

export function clearAdminSession() {
  removeAdminToken();
}
